import { UserMinus, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RoleName } from '@/components/RoleName'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Field, Select } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Tag } from '@/components/ui/Tag'
import { describeProblem } from '@/lib/problems'
import {
  useAssignTeamMember,
  useCaseTeam,
  useRemoveTeamMember,
} from '@/features/cases/api/useCase'
import { useColleagues } from '@/features/directories/api/useColleagues'
import { useRoleLabel } from '@/features/settings/api/useRoleNames'
import type { CaseDetail, CaseTeamMember } from '@/types/domain'

/* The internal team on a case (Story 3.6, FR-69).
 *
 * Beside ownership rather than on a settings screen, which is what FR-69 asks
 * for in as many words: assignment is an action ON THE RECORD, and a user who
 * has to leave the case to staff it will not staff it.
 *
 * The team is not decoration — it IS the row-scope predicate (AD-5). Somebody
 * added here can see the shipment from their next request; somebody removed
 * loses it just as fast (FR-56). That is why neither mutation is optimistic:
 * a row rendered before the server agreed would be a screen claiming a
 * colleague can see a case they cannot.
 */
export function CaseTeamSection({ record }: { record: CaseDetail }) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)

  /* Seeded from the case's own payload so the section paints with the header
     rather than after a second round trip, then kept fresh by its own query —
     assigning somebody must re-render this without refetching the legs, the
     files and the money beside it. */
  const { data } = useCaseTeam(record.id)
  const team = data ?? record.team ?? []

  const assign = useAssignTeamMember(record.id)
  const remove = useRemoveTeamMember(record.id)

  /* Whichever write last failed is the one to report. The refusals here are
     the point of the story — a deactivated account (FR-57) and the last
     assignee — and both must reach the user as words rather than as a row that
     silently fails to appear. */
  const failure = assign.error ?? remove.error

  return (
    <Card>
      <CardHeader
        action={
          <Button
            variant="secondary"
            size="sm"
            iconLeft={UserPlus}
            onClick={() => {
              assign.reset()
              remove.reset()
              setAdding(true)
            }}
          >
            {t('cases.team.assign')}
          </Button>
        }
      >
        <CardTitle className="flex items-center gap-2">
          <Users size={15} className="text-text-muted" aria-hidden />
          {t('cases.team.title')}
        </CardTitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-1 pt-2">
        {/* What adding somebody DOES, said once at the top. The section stands
            on its own tab now rather than under the overview's facts, so the
            one thing a reader must not have to guess — that membership IS the
            access, not a note about who is involved — is stated rather than
            left to the assign sheet's hint, which is only read by somebody who
            already decided to assign. */}
        <p className="text-text-muted pb-1 text-[12.5px] leading-snug font-light">
          {t('cases.team.hint')}
        </p>

        {failure ? (
          <p className="text-attention pb-1 text-[12.5px] leading-snug">
            {t(describeProblem(failure).detailKey)}
          </p>
        ) : null}

        {team.map((member) => (
          <TeamRow
            key={member.userId}
            member={member}
            pending={remove.isPending}
            onRemove={() => {
              assign.reset()
              remove.mutate(member.userId)
            }}
          />
        ))}
      </CardBody>

      <AssignSheet
        open={adding}
        onClose={() => setAdding(false)}
        team={team}
        pending={assign.isPending}
        onAssign={(userId) =>
          assign.mutate(userId, { onSuccess: () => setAdding(false) })
        }
      />
    </Card>
  )
}

function TeamRow({
  member,
  pending,
  onRemove,
}: {
  member: CaseTeamMember
  pending: boolean
  onRemove: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="border-line-hair flex items-center gap-2.5 border-b py-2 last:border-b-0">
      <span className="text-text-heading min-w-0 flex-1 truncate text-[13px]">
        {member.name}
      </span>
      <span className="text-text-muted text-[12px]">
        <RoleName role={member.role} />
      </span>
      {member.owner ? <Tag tone="neutral">{t('cases.team.owner')}</Tag> : null}

      {/* Offered on every member INCLUDING the owner. The rule that protects a
          case is "not the LAST one", not "not the owner" — an owner who has
          handed the shipment over should be able to step off it — and that
          rule is the server's to enforce, because only a transaction can ask
          whether anyone would be left. */}
      <Button
        variant="ghost"
        size="sm"
        iconLeft={UserMinus}
        disabled={pending}
        aria-label={t('cases.team.remove', { name: member.name })}
        onClick={onRemove}
      />
    </div>
  )
}

/* The picker.
 *
 * Deactivated accounts are absent from it (FR-57) — but that is a COURTESY,
 * not the control: the server refuses the assignment regardless, because an
 * account can be deactivated between this list rendering and the click. */
function AssignSheet({
  open,
  onClose,
  team,
  pending,
  onAssign,
}: {
  open: boolean
  onClose: () => void
  team: CaseTeamMember[]
  pending: boolean
  onAssign: (userId: string) => void
}) {
  const { t } = useTranslation()
  /* The staff ROSTER, not the admin user list.
   *
   * This used to read useUsers(), which fetches /admin/users — an endpoint the
   * server gates on admin.users.manage. Every non-founder who opened this sheet
   * therefore got a 403 and an empty picker, and an empty picker reads as
   * "nobody to assign" rather than as a permission error, which is why it went
   * unnoticed: this component's tests mock the hook, so the guard was never in
   * the loop.
   *
   * useColleagues() reads GET /api/users, which any signed-in user may call and
   * which returns only accounts that can actually be staffed. */
  const { data: colleagues } = useColleagues()
  const roleName = useRoleLabel()
  const [chosen, setChosen] = useState('')

  const assigned = new Set(team.map((member) => member.userId))
  // No isActive filter: the roster endpoint has already dropped accounts that
  // cannot sign in (FR-57), so a second check here would be a copy of a rule
  // the server owns.
  const options = (colleagues ?? [])
    .filter((person) => !assigned.has(person.id))
    .map((person) => ({
      value: person.id,
      label: person.name,
      /* Role and office as the second line (#136 N-27): two colleagues sharing
         a name is the ordinary case across two offices, and a list of bare
         names cannot be chosen from. The role is its display name — a custom
         role's own words — and the same line the quote team's picker prints
         (QuoteTeamSection's AssignSheet). */
      hint: `${roleName(person.role)} · ${person.office}`,
    }))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.team.assign')}
      pending={pending}
      disabled={!chosen}
      onSubmit={() => onAssign(chosen)}
    >
      <Field label={t('cases.team.colleague')} required>
        {(props) => (
          <Select
            value={chosen}
            onChange={(value) => setChosen(value)}
            options={options}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>
      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('cases.team.assignHint')}
      </p>
    </FormSheet>
  )
}
